import React, { useState, useEffect } from 'react';
import { Megaphone, Loader2, Save, Eye } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Aperçu HTML dans une modale (email/bannière) — réutilisable partout où un rendu final
 * doit être validé visuellement par l'admin avant envoi/publication. */
export const EmailPreviewModal = ({ html, subject, onClose }) => (
  <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="email-preview-modal">
    <div className="w-full max-w-xl bg-zinc-900 border border-white/10 rounded-2xl overflow-hidden max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between gap-3 p-3.5 border-b border-white/10 shrink-0">
        <p className="text-white text-sm font-bold truncate">{subject || 'Aperçu email'}</p>
        <button onClick={onClose} className="text-zinc-400 hover:text-white text-sm px-2" data-testid="email-preview-close-btn">Fermer</button>
      </div>
      <iframe title="email-preview" srcDoc={html} className="flex-1 w-full bg-white" style={{ minHeight: 480 }} data-testid="email-preview-iframe" />
    </div>
  </div>
);

/** Panneau admin "Réglages du site" — bandeau "essai gratuit" personnalisable
 * (texte FR/EN, lien, activation), affiché sur toutes les pages publiques/app. */
export const AdminSiteSettingsPanel = ({ csrfToken }) => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/settings/banner`, { credentials: 'include' })
      .then((r) => r.json()).then(setCfg).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/settings/banner`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify(cfg),
      });
      if (!res.ok) throw new Error();
      toast.success('Bannière mise à jour.');
    } catch {
      toast.error('Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-site-settings-panel">
      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5 space-y-4">
        <h3 className="flex items-center gap-2 font-bold text-white"><Megaphone className="w-5 h-5 text-emerald-400" /> Bandeau "essai gratuit" (toutes les pages)</h3>

        <label className="flex items-center gap-2.5 text-sm text-zinc-300">
          <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="w-4 h-4 accent-primary" data-testid="banner-enabled-checkbox" />
          Bandeau activé
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-zinc-500 text-xs mb-1 block">Texte (Français)</label>
            <input value={cfg.text_fr} onChange={(e) => setCfg({ ...cfg, text_fr: e.target.value })} maxLength={160}
              className="w-full px-3 py-2.5 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm" data-testid="banner-text-fr-input" />
          </div>
          <div>
            <label className="text-zinc-500 text-xs mb-1 block">Texte (Anglais)</label>
            <input value={cfg.text_en} onChange={(e) => setCfg({ ...cfg, text_en: e.target.value })} maxLength={160}
              className="w-full px-3 py-2.5 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm" data-testid="banner-text-en-input" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-zinc-500 text-xs mb-1 block">Emoji</label>
            <input value={cfg.emoji} onChange={(e) => setCfg({ ...cfg, emoji: e.target.value })} maxLength={8}
              className="w-full px-3 py-2.5 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm" data-testid="banner-emoji-input" />
          </div>
          <div>
            <label className="text-zinc-500 text-xs mb-1 block">Lien</label>
            <input value={cfg.link} onChange={(e) => setCfg({ ...cfg, link: e.target.value })} maxLength={200}
              className="w-full px-3 py-2.5 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm" data-testid="banner-link-input" />
          </div>
        </div>

        <div className="rounded-xl overflow-hidden border border-white/10">
          <div className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 text-white h-9 flex items-center justify-center text-xs font-bold px-3">
            {cfg.emoji} {cfg.text_fr}
          </div>
        </div>

        <div className="flex gap-2">
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-50" data-testid="save-banner-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSiteSettingsPanel;
